import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { 
  X, 
  Edit2, 
  User, 
  Building, 
  Phone, 
  Mail, 
  Calendar, 
  DollarSign, 
  Target, 
  Star, 
  TrendingUp,
  Globe,
  MapPin,
  Clock,
  FileText,
  Award,
  Users,
  ExternalLink
} from 'lucide-react';
import AttachmentServiceV2 from '../../components/Attachments/AttachmentServiceV2';
import entityRelationService from '../../services/entityRelationService';
import EntityActivities from '../../components/EntityManagement/EntityActivities';
import CustomAttributesDisplay from '../../components/CustomAttributes/CustomAttributesDisplay';
import { extractCustomAttributeFields } from '../../components/CustomAttributes/utils';
import { useEntityConfig } from '../../hooks/useEntityConfig';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../utils/api';

const LeadDetailModal = ({ lead, isOpen, onClose, onEdit }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const tenantCode =
    user?.selectedTenant?.code ||
    user?.selectedTenant?.tenant_code ||
    user?.tenantCode ||
    null;
  const { config: entityConfig } = useEntityConfig('lead');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [leadData, setLeadData] = useState(lead);
  const [entityRelations, setEntityRelations] = useState([]);
  const customAttributeFields = useMemo(() => {
    if (!entityConfig) return [];
    try {
      return extractCustomAttributeFields(entityConfig, {
        tenantCode,
        includeGlobal: true
      });
    } catch (err) {
      console.error('LeadDetailModal: Failed to extract custom attribute fields', err);
      return [];
    }
  }, [entityConfig, tenantCode]);

  const leadAttributes = useMemo(() => {
    const raw = leadData?.Attributes ?? leadData?.attributes;
    if (!raw) return {};
    if (typeof raw === 'object' && !Array.isArray(raw)) {
      return raw;
    }
    if (typeof raw === 'string') {
      try {
        return JSON.parse(raw);
      } catch (error) {
        console.warn('LeadDetailModal: Failed to parse attributes JSON', error);
        return {};
      }
    }
    return {};
  }, [leadData?.Attributes, leadData?.attributes, leadData]);

  useEffect(() => {
    if (isOpen && lead && !leadData) {
      fetchLeadDetails();
    } else if (lead) {
      setLeadData(lead);
    }
  }, [isOpen, lead]);

  const fetchLeadDetails = async () => {
    try {
      setLoading(true);
      const data = await api.get(`/leads/${lead.LeadID}`);
      setLeadData(data.lead || data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Load unified entity relations similar to LeadDetail.js
  useEffect(() => {
    const loadEntityRelations = async (leadId) => {
      try {
        const relationsResp = await entityRelationService.getRelatedEntities('Lead', leadId, { direction: 'both', timeoutMs: 10000 });
        const relations = Array.isArray(relationsResp) ? relationsResp : (relationsResp?.relations || []);
        const truncate = (text, max = 80) => {
          if (!text) return '';
          const trimmed = String(text).trim();
          return trimmed.length > max ? `${trimmed.slice(0, max)}…` : trimmed;
        };

        const mapped = relations.map(rel => {
          const srcTypeRaw = rel.SourceEntityType || rel.sourceEntityType;
          const tgtTypeRaw = rel.TargetEntityType || rel.targetEntityType;
          const srcId = rel.SourceEntityID || rel.sourceEntityID;
          const tgtId = rel.TargetEntityID || rel.targetEntityID;
          const isLeadSource = (srcTypeRaw || '').toString().toLowerCase() === 'lead';
          const entityTypeRaw = isLeadSource ? tgtTypeRaw : srcTypeRaw;
          const entityTypeLower = (entityTypeRaw || '').toString().toLowerCase();
          const entityType = entityTypeLower === 'customer' ? 'Customer'
            : entityTypeLower === 'lead' ? 'Lead'
            : entityTypeLower === 'opportunity' ? 'Opportunity'
            : entityTypeLower === 'activity' ? 'Activity'
            : entityTypeLower === 'salenote' || entityTypeLower === 'salesnote' ? 'SaleNote'
            : (entityTypeRaw || '');
          const entityId = isLeadSource ? tgtId : srcId;

          // Derive display name where possible
          let entityName = '';
          switch (entityType) {
            case 'Customer':
              entityName = rel.CustomerName || `${rel.FirstName || ''} ${rel.LastName || ''}`.trim() || rel.CompanyName || `Customer ${entityId}`;
              break;
            case 'Lead':
              entityName = rel.Title || rel.ContactName || rel.CompanyName || `Lead ${entityId}`;
              break;
            case 'Opportunity':
              entityName = rel.OpportunityName || rel.Title || `Opportunity ${entityId}`;
              break;
            case 'Activity':
              entityName = rel.Subject || `${rel.ActivityType || 'Activity'} ${entityId}`;
              break;
            case 'SaleNote':
              entityName = truncate((rel.NoteText ? String(rel.NoteText).split('\n')[0] : '')) || `Sale Note ${entityId}`;
              break;
            default:
              entityName = `${entityType} ${entityId}`;
          }

          // Try to extract additional info from relation data
          let additionalInfo = '';
          switch (entityType) {
            case 'Customer':
              additionalInfo = rel.CompanyName || rel.PersonEmail || rel.Industry || '';
              break;
            case 'Lead':
              additionalInfo = rel.CompanyName || rel.ContactEmail || rel.Status || '';
              break;
            case 'Opportunity':
              additionalInfo = rel.Stage || (rel.Amount ? `$${rel.Amount}` : '') || '';
              break;
            case 'Activity':
              additionalInfo = rel.ActivityType || rel.DueDate || '';
              break;
            case 'SaleNote':
              additionalInfo = rel.CreatedAt ? new Date(rel.CreatedAt).toLocaleDateString() : '';
              break;
          }

          return {
            EntityType: entityType,
            EntityID: entityId,
            EntityName: entityName,
            AdditionalInfo: additionalInfo,
            RelationID: rel.RelationID || rel.relationId,
            RelationType: rel.RelationType || rel.relationType
          };
        }).filter(m => m.EntityType && m.EntityID);

        // Enrich names for items that still look like defaults (only type + ID)
        const needsEnrichment = mapped.filter(m => !m.EntityName || m.EntityName === `${m.EntityType} ${m.EntityID}`);
        if (needsEnrichment.length > 0) {
          const enriched = await Promise.all(mapped.map(async (m) => {
            const hasDefaultName = !m.EntityName || m.EntityName === `${m.EntityType} ${m.EntityID}`;
            if (!hasDefaultName) return m;
            try {
              let detail;
              let additionalInfo = m.AdditionalInfo || '';
              if (m.EntityType === 'Customer') {
                detail = await api.get(`/customers/${m.EntityID}`);
                const cust = detail?.customer || detail || {};
                const name = cust.CustomerType === 'Person'
                  ? `${cust.FirstName || ''} ${cust.LastName || ''}`.trim() || cust.CustomerName
                  : cust.CompanyName || cust.CustomerName;
                if (!additionalInfo) {
                  additionalInfo = cust.CustomerType === 'Person' 
                    ? (cust.CompanyName || cust.PersonEmail || '')
                    : (cust.Industry || cust.PersonEmail || '');
                }
                return { ...m, EntityName: name || m.EntityName, AdditionalInfo: additionalInfo };
              } else if (m.EntityType === 'Lead') {
                detail = await api.get(`/leads/${m.EntityID}`);
                const ld = detail?.lead || detail || {};
                const name = ld.Title || ld.ContactName || ld.CompanyName;
                if (!additionalInfo) {
                  additionalInfo = ld.CompanyName || ld.ContactEmail || ld.Status || '';
                }
                return { ...m, EntityName: name || m.EntityName, AdditionalInfo: additionalInfo };
              } else if (m.EntityType === 'Opportunity') {
                detail = await api.get(`/opportunities/${m.EntityID}`);
                const opp = detail?.opportunity || detail || {};
                const name = opp.OpportunityName || opp.CustomerName;
                if (!additionalInfo) {
                  additionalInfo = opp.Stage || (opp.Amount ? `$${opp.Amount || 0}` : '') || '';
                }
                return { ...m, EntityName: name || m.EntityName, AdditionalInfo: additionalInfo };
              } else if (m.EntityType === 'Activity') {
                detail = await api.get(`/activities/${m.EntityID}`);
                const act = detail?.activity || detail || {};
                const name = act.Subject || `${act.ActivityType || 'Activity'} ${m.EntityID}`;
                if (!additionalInfo) {
                  additionalInfo = act.ActivityType || act.DueDate || '';
                }
                return { ...m, EntityName: name || m.EntityName, AdditionalInfo: additionalInfo };
              } else if (m.EntityType === 'SaleNote') {
                try {
                  detail = await api.get(`/sales-notes/${m.EntityID}`);
                  const sn = detail?.saleNote || detail || {};
                  const name = truncate(sn.NoteText ? String(sn.NoteText).split('\n')[0] : (sn.Title || ''));
                  if (!additionalInfo) {
                    additionalInfo = sn.CreatedAt ? new Date(sn.CreatedAt).toLocaleDateString() : '';
                  }
                  return { ...m, EntityName: name || m.EntityName, AdditionalInfo: additionalInfo };
                } catch {
                  return m;
                }
              }
            } catch (e) {
              console.warn('⚠️ [LeadDetailModal] Failed to enrich relation name for', m, e);
              return m;
            }
            return m;
          }));
          setEntityRelations(enriched);
        } else {
          setEntityRelations(mapped);
        }
      } catch (e) {
        console.warn('[LeadDetailModal] Failed to load entity relations:', e);
        setEntityRelations([]);
      }
    };

    if (leadData?.LeadID) {
      loadEntityRelations(leadData.LeadID);
    }
  }, [leadData?.LeadID]);

  // Helpers for entity relations UI (icons, paths, and colors)
  const getEntityColors = (entityType) => {
    switch (entityType) {
      case 'Customer':
        // Customer (default to Company = green)
        return {
          bg: 'bg-green-50 dark:bg-green-900/20',
          border: 'border-green-200 dark:border-green-800',
          hoverBg: 'hover:bg-green-100 dark:hover:bg-green-900/30',
          icon: 'text-green-600 dark:text-green-400',
          badge: 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
        };
      case 'Lead':
        // Lead = red
        return {
          bg: 'bg-red-50 dark:bg-red-900/20',
          border: 'border-red-200 dark:border-red-800',
          hoverBg: 'hover:bg-red-100 dark:hover:bg-red-900/30',
          icon: 'text-red-600 dark:text-red-400',
          badge: 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200'
        };
      case 'Opportunity':
        // Opportunity = violet
        return {
          bg: 'bg-violet-50 dark:bg-violet-900/20',
          border: 'border-violet-200 dark:border-violet-800',
          hoverBg: 'hover:bg-violet-100 dark:hover:bg-violet-900/30',
          icon: 'text-violet-600 dark:text-violet-400',
          badge: 'bg-violet-100 dark:bg-violet-900 text-violet-800 dark:text-violet-200'
        };
      case 'Activity':
        return {
          bg: 'bg-orange-50 dark:bg-orange-900/20',
          border: 'border-orange-200 dark:border-orange-800',
          hoverBg: 'hover:bg-orange-100 dark:hover:bg-orange-900/30',
          icon: 'text-orange-600 dark:text-orange-400',
          badge: 'bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200'
        };
      case 'SaleNote':
        // SaleNote = yellow
        return {
          bg: 'bg-yellow-50 dark:bg-yellow-900/20',
          border: 'border-yellow-200 dark:border-yellow-800',
          hoverBg: 'hover:bg-yellow-100 dark:hover:bg-yellow-900/30',
          icon: 'text-yellow-600 dark:text-yellow-400',
          badge: 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200'
        };
      default:
        return {
          bg: 'bg-gray-50 dark:bg-gray-700',
          border: 'border-gray-200 dark:border-gray-700',
          hoverBg: 'hover:bg-gray-100 dark:hover:bg-gray-600',
          icon: 'text-gray-600 dark:text-gray-400',
          badge: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
        };
    }
  };

  const getEntityIcon = (entityType) => {
    const colors = getEntityColors(entityType);
    const iconClass = `h-4 w-4 ${colors.icon}`;
    
    switch (entityType) {
      case 'Customer': return <Building className={iconClass} />;
      case 'Lead': return <User className={iconClass} />;
      case 'Opportunity': return <Target className={iconClass} />;
      case 'Activity': return <Calendar className={iconClass} />;
      case 'SaleNote': return <FileText className={iconClass} />;
      default: return <FileText className={iconClass} />;
    }
  };

  const getEntityPath = (entityType, entityId) => {
    switch (entityType) {
      case 'Customer': return `/customers/${entityId}`;
      case 'Lead': return `/leads/${entityId}`;
      case 'Opportunity': return `/opportunities/${entityId}`;
      case 'Activity': return `/activities/${entityId}`;
      case 'SaleNote': return `/notes?noteId=${entityId}`;
      default: return '#';
    }
  };

  const handleEdit = () => {
    if (onEdit) {
      onEdit(leadData);
    }
  };

  const handleClose = () => {
    setError(null);
    setLeadData(null);
    onClose();
  };

  if (!isOpen) return null;

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'Critical': return 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/60 dark:text-white dark:border-red-700/60';
      case 'High': return 'bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-900/60 dark:text-white dark:border-orange-700/60';
      case 'Medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200 dark:bg-amber-900/60 dark:text-white dark:border-amber-700/60';
      case 'Low': return 'bg-green-100 text-green-800 border-green-200 dark:bg-emerald-900/60 dark:text-white dark:border-emerald-700/60';
      default: return 'bg-gray-100 text-gray-800 border-gray-200 dark:bg-slate-800 dark:text-white dark:border-slate-600';
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600 dark:text-green-400';
    if (score >= 60) return 'text-yellow-600 dark:text-yellow-400';
    if (score >= 40) return 'text-orange-600 dark:text-orange-400';
    return 'text-red-600 dark:text-red-400';
  };

  const getQualificationColor = (level) => {
    switch (level) {
      case 'Hot': return 'bg-red-100 text-red-800 border-red-200 dark:bg-red-900/60 dark:text-white dark:border-red-700/60';
      case 'Warm': return 'bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-900/60 dark:text-white dark:border-orange-700/60';
      case 'Cold': return 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/60 dark:text-white dark:border-blue-700/60';
      case 'Qualified': return 'bg-green-100 text-green-800 border-green-200 dark:bg-emerald-900/60 dark:text-white dark:border-emerald-700/60';
      default: return 'bg-gray-100 text-gray-800 border-gray-200 dark:bg-slate-800 dark:text-white dark:border-slate-600';
    }
  };

  const formatCurrency = (amount, currency = 'USD') => {
    if (!amount) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  const formatLeadName = (lead) => {
    return lead.ContactName || lead.CompanyName || `Lead #${lead.LeadID}`;
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md mx-4">
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <p className="text-red-600 dark:text-red-400">{error}</p>
          </div>
          <button
            onClick={handleClose}
            className="mt-4 w-full bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600"
          >
            {t('common.close')}
          </button>
        </div>
      </div>
    );
  }

  if (!leadData) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md mx-4">
          <div className="bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-4">
            <p className="text-gray-600 dark:text-gray-400">{t('leads.leadNotFound', 'Lead not found')}</p>
          </div>
          <button
            onClick={handleClose}
            className="mt-4 w-full bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600"
          >
            {t('common.close')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl max-w-5xl w-full max-h-[95vh] overflow-hidden">
        {/* Compact Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3">
            <div>
              <h1 className="text-xl font-bold text-gray-900 dark:text-white">
                {formatLeadName(leadData)}
              </h1>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {leadData.CompanyName && `${leadData.CompanyName} • `}{leadData.ContactName}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={handleEdit}
              className="bg-blue-600 dark:bg-blue-500 text-white px-3 py-1.5 rounded-lg flex items-center space-x-2 hover:bg-blue-700 dark:hover:bg-blue-600 text-sm"
            >
              <Edit2 className="h-4 w-4" />
              <span>{t('common.edit')}</span>
            </button>
            <button
              onClick={handleClose}
              className="p-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              <X className="h-4 w-4 text-gray-600 dark:text-gray-300" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto max-h-[calc(95vh-80px)]">
          <div className="p-4">
            {/* Key Metrics Row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <div className="bg-white dark:bg-gray-800 p-3 rounded-lg shadow border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-600 dark:text-gray-300">{t('leads.score')}</p>
                    <p className={`text-lg font-bold ${getScoreColor(leadData.Score)}`}>
                      {leadData.Score || 0}
                    </p>
                  </div>
                  <Star className="h-5 w-5 text-yellow-500 dark:text-yellow-400" />
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 p-3 rounded-lg shadow border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-600 dark:text-gray-300">{t('leads.priority')}</p>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">{leadData.Priority}</p>
                  </div>
                  <TrendingUp className="h-5 w-5 text-blue-500 dark:text-blue-400" />
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 p-3 rounded-lg shadow border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-600 dark:text-gray-300">{t('leads.status')}</p>
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">{leadData.Status}</p>
                  </div>
                  <Target className="h-5 w-5 text-green-500 dark:text-green-400" />
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 p-3 rounded-lg shadow border border-gray-200 dark:border-gray-700">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-600 dark:text-gray-300">{t('leads.estimatedBudget')}</p>
                    <p className="text-sm font-semibold text-green-600 dark:text-green-400">
                      {formatCurrency(leadData.EstimatedBudget, leadData.Currency)}
                    </p>
                  </div>
                  <DollarSign className="h-5 w-5 text-green-500 dark:text-green-400" />
                </div>
              </div>
            </div>

            {/* Status Badges */}
            <div className="flex items-center space-x-2 mb-4">
              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getQualificationColor(leadData.QualificationLevel)}`}>
                {leadData.QualificationLevel || t('leads.notSet')}
              </span>
              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getPriorityColor(leadData.Priority)}`}>
                {leadData.Priority}
              </span>
              <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/60 dark:text-blue-300">
                {leadData.Status}
              </span>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
              {/* Main Content - 3 columns */}
              <div className="lg:col-span-3 space-y-4">
                {/* Basic & Contact Info Combined */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-4">
                  <div className="flex items-center space-x-2 mb-3">
                    <User className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                    <h3 className="text-base font-medium text-gray-900 dark:text-white">{t('leads.basicInfo', 'Basic Information')}</h3>
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('leads.title')}
                      </label>
                      <p className="text-sm text-gray-900 dark:text-white">{leadData.Title || '-'}</p>
                    </div>
                    
                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('leads.leadSource')}
                      </label>
                      <p className="text-sm text-gray-900 dark:text-white">{leadData.LeadSource || '-'}</p>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('leads.contactName')}
                      </label>
                      <p className="text-sm text-gray-900 dark:text-white">{leadData.ContactName || '-'}</p>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('leads.jobTitle')}
                      </label>
                      <p className="text-sm text-gray-900 dark:text-white">{leadData.JobTitle || '-'}</p>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('leads.companyName')}
                      </label>
                      <p className="text-sm text-gray-900 dark:text-white">{leadData.CompanyName || '-'}</p>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {t('leads.industry')}
                      </label>
                      <p className="text-sm text-gray-900 dark:text-white">{leadData.Industry || '-'}</p>
                    </div>
                  </div>
                </div>

                {/* Contact Details */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-4">
                  <div className="flex items-center space-x-2 mb-3">
                    <Phone className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                    <h3 className="text-base font-medium text-gray-900 dark:text-white">{t('leads.contactInfo', 'Contact Information')}</h3>
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div className="flex items-center space-x-2">
                      <Mail className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                      <div>
                        <p className="text-xs text-gray-600 dark:text-gray-400">{t('leads.email')}</p>
                        <p className="text-sm text-gray-900 dark:text-white">{leadData.ContactEmail || '-'}</p>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      <Phone className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                      <div>
                        <p className="text-xs text-gray-600 dark:text-gray-400">{t('leads.phone')}</p>
                        <p className="text-sm text-gray-900 dark:text-white">{leadData.ContactPhone || '-'}</p>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      <Globe className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                      <div>
                        <p className="text-xs text-gray-600 dark:text-gray-400">{t('leads.website')}</p>
                        <p className="text-sm text-gray-900 dark:text-white">{leadData.Website || '-'}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Timeline & Financial Combined */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-4">
                  <div className="flex items-center space-x-2 mb-3">
                    <Calendar className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                    <h3 className="text-base font-medium text-gray-900 dark:text-white">{t('leads.timeline', 'Timeline & Financial')}</h3>
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-xs text-gray-600 dark:text-gray-400">{t('leads.createdAt')}</p>
                      <p className="text-sm text-gray-900 dark:text-white">{formatDate(leadData.CreatedAt)}</p>
                    </div>
                    
                    <div>
                      <p className="text-xs text-gray-600 dark:text-gray-400">{t('leads.expectedCloseDate')}</p>
                      <p className="text-sm text-gray-900 dark:text-white">{formatDate(leadData.ExpectedCloseDate)}</p>
                    </div>

                    <div>
                      <p className="text-xs text-gray-600 dark:text-gray-400">{t('leads.estimatedBudget')}</p>
                      <p className="text-sm text-green-600 dark:text-green-400 font-semibold">
                        {formatCurrency(leadData.EstimatedBudget, leadData.Currency)}
                      </p>
                    </div>

                    <div>
                      <p className="text-xs text-gray-600 dark:text-gray-400">{t('leads.currency')}</p>
                      <p className="text-sm text-gray-900 dark:text-white">{leadData.Currency || '-'}</p>
                    </div>
                  </div>
                </div>

                {/* Notes and Description */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-4">
                  <div className="flex items-center space-x-2 mb-3">
                    <FileText className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                    <h3 className="text-base font-medium text-gray-900 dark:text-white">{t('leads.notesAndDescription', 'Notes & Description')}</h3>
                  </div>
                  
                  <div className="space-y-3">
                    <div>
                      <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">{t('leads.description')}</p>
                      <p className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap">{leadData.Description || '-'}</p>
                    </div>
                    
                    <div>
                      <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">{t('leads.notes')}</p>
                      <p className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap">{leadData.Notes || '-'}</p>
                    </div>

                    <div>
                      <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">{t('leads.requirements')}</p>
                      <p className="text-sm text-gray-900 dark:text-white whitespace-pre-wrap">{leadData.Requirements || '-'}</p>
                    </div>
                  </div>
                </div>

                {customAttributeFields.length > 0 && (
                  <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-4">
                    <CustomAttributesDisplay
                      fields={customAttributeFields}
                      attributes={leadAttributes}
                      title={t('leads.additionalDataSection', 'Lead Additional Data')}
                      entityName="lead"
                      onFieldsUpdated={async () => {
                        if (lead?.LeadID) {
                          window.location.reload();
                        }
                      }}
                    />
                  </div>
                )}

                {/* Activities */}
                <EntityActivities
                  entityType="Lead"
                  entityId={leadData.LeadID}
                  entityName={formatLeadName(leadData)}
                />

                {/* Attachments */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-4">
                  <AttachmentServiceV2 
                    entityType="Lead" 
                    entityId={leadData.LeadID} 
                    onAttachmentChange={() => {
                      // Optional: Refresh lead data or show notification
                    }}
                  />
                </div>
              </div>

              {/* Compact Sidebar - 1 column */}
              <div className="space-y-4">
                {/* Entity Relations */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-4">
                  <div className="flex items-center space-x-2 mb-3">
                    <Users className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                    <h3 className="text-base font-medium text-gray-900 dark:text-white">{t('common.relatedItems', 'Relationships')}</h3>
                  </div>
                  <div>
                    {entityRelations.length === 0 ? (
                      <p className="text-xs text-gray-500 dark:text-gray-400">{t('leads.noEntityRelations', 'No entity relations')}</p>
                    ) : (
                      <div className="space-y-1.5">
                        {entityRelations.map((rel, idx) => (
                          <div
                            key={rel.RelationID || `${rel.EntityType}-${rel.EntityID}-${idx}`}
                            className="flex items-center space-x-2 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                            onClick={() => {
                              const path = getEntityPath(rel.EntityType, rel.EntityID);
                              if (path !== '#') {
                                onClose();
                                navigate(path);
                              }
                            }}
                          >
                            {getEntityIcon(rel.EntityType)}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{rel.EntityName}</p>
                              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                {rel.AdditionalInfo ? (
                                  <span>{rel.AdditionalInfo} • {rel.EntityType}</span>
                                ) : (
                                  <span>{rel.EntityType} • ID: {rel.EntityID}</span>
                                )}
                              </p>
                            </div>
                            <ExternalLink className="h-3.5 w-3.5 text-gray-400" />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                {/* Lead Score */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-4">
                  <div className="flex items-center space-x-2 mb-3">
                    <Star className="h-4 w-4 text-yellow-400" />
                    <h3 className="text-base font-medium text-gray-900 dark:text-white">{t('leads.leadScore', 'Lead Score')}</h3>
                  </div>
                  <div className="text-center">
                    <div className={`text-3xl font-bold ${getScoreColor(leadData.Score)}`}>
                      {leadData.Score || 0}
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 mt-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full" 
                        style={{ width: `${leadData.Score || 0}%` }}
                      ></div>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{t('leads.scoreOutOf100')}</p>
                  </div>
                </div>

                {/* Conversion Status */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-4">
                  <div className="flex items-center space-x-2 mb-3">
                    <Target className="h-4 w-4 text-purple-400" />
                    <h3 className="text-base font-medium text-gray-900 dark:text-white">{t('leads.conversionStatus', 'Conversion Status')}</h3>
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-600 dark:text-gray-400">{t('leads.toOpportunity')}</span>
                      <span className={`text-xs px-2 py-1 rounded-full ${leadData.ConvertedToOpportunity ? 'bg-green-100 text-green-800 dark:bg-green-900/60 dark:text-green-300' : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'}`}>
                        {leadData.ConvertedToOpportunity ? t('leads.yes') : t('leads.no')}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-600 dark:text-gray-400">{t('leads.toCustomer')}</span>
                      <span className={`text-xs px-2 py-1 rounded-full ${leadData.ConvertedToCustomer ? 'bg-green-100 text-green-800 dark:bg-green-900/60 dark:text-green-300' : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'}`}>
                        {leadData.ConvertedToCustomer ? t('leads.yes') : t('leads.no')}
                      </span>
                    </div>
                    {leadData.ConversionDate && (
                      <div className="pt-2 border-t border-gray-200 dark:border-gray-600">
                        <span className="text-xs text-gray-600 dark:text-gray-400">{t('leads.conversionDateLabel')}:</span>
                        <p className="text-xs font-medium text-gray-900 dark:text-white">{formatDate(leadData.ConversionDate)}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Additional Info */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-4">
                  <div className="flex items-center space-x-2 mb-3">
                    <Award className="h-4 w-4 text-blue-400" />
                    <h3 className="text-base font-medium text-gray-900 dark:text-white">{t('leads.additionalInfo')}</h3>
                  </div>
                  <div className="space-y-2">
                    <div>
                      <p className="text-xs text-gray-600 dark:text-gray-400">{t('leads.companySize')}</p>
                      <p className="text-sm text-gray-900 dark:text-white">{leadData.CompanySize || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-600 dark:text-gray-400">{t('leads.lastContact')}</p>
                      <p className="text-sm text-gray-900 dark:text-white">{formatDate(leadData.LastContactDate)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-600 dark:text-gray-400">{t('leads.nextFollowUpShort')}</p>
                      <p className="text-sm text-gray-900 dark:text-white">{formatDate(leadData.NextFollowUpDate)}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeadDetailModal;
